<template>
  <AppLoading v-if="isLoading"></AppLoading>
  <main
    v-else
    class="mb-2"
  >
    <section
      v-if="!value || !value.length"
      class="border rounded-5 bg-light p-3 text-center text-muted"
    >
      No Program Specializations
    </section>
    <section v-else>
      <CommonAccordion
        v-for="(specialization, index) of specializations"
        :key="index"
        :initiallyOpen="editing === index"
        class="bg-light rounded-5 mb-2"
        :data-test="`specializations_${index}`"
      >
        <template #header>
          <div class="py-3">
            {{ getSpecializationName(specialization) }}
          </div>
        </template>
        <template #content>
          <FormProgramSpecialization
            :value="specialization"
            :config="config"
            :index="index"
            :formValue="formValue"
            :nestedMode="nestedMode"
            :editable="!disabled && userAllowedToEdit"
            class="p-2"
            style="background-color:#FCFCFC;"
            @input="updateSpecialization(index, $event)"
            @delete="deleteItem(index)"
          />
        </template>
      </CommonAccordion>
    </section>
    <AppButton
      v-if="!disabled && userAllowedToEdit"
      :raised="true"
      class="mt-2"
      @click="addSpecialization"
    >
      <AppIcon
        name="add"
        class="mr-2"
      />
      New Specialization
    </AppButton>
  </main>
</template>

<script>
import { ref, toRefs } from '@vue/composition-api'
import FormMixin from '@/components/Forms/FormMixin'
import FormProgramSpecialization from '@/components/Forms/FormProgramSpecialization'
// eslint-disable-next-line no-restricted-imports
import CommonAccordion from '@/components/Common/CommonAccordion'
import { userHasAnyAllowedRole } from '@shared/permissions/validators'
import { mapGetters } from 'vuex'
import { putAt } from '@shared/utils/array'
import { fillSubfieldsWithDefaults } from '@/utils/subfields'

export default {
  name: 'FormProgramSpecializations',
  mixins: [ FormMixin ],
  components: {
    FormProgramSpecialization,
    CommonAccordion,
  },
  props: {
    rolesAllowedToEdit: {
      type: Array,
      default: () => ([]),
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
    nestedMode: {
      type: Boolean,
      default: true,
    },
  },
  setup (props, { root, emit }) {
    const { value: specializations } = toRefs(props)
    const editing = ref(null)
    const isLoading = ref(true)

    async function fetchTerms () {
      await root.$store.dispatch('loadTerms', {
        school: root.$store.getters.school,
      })
      isLoading.value = false
    }
    fetchTerms()

    function getSpecializationName (item = {}) {
      return [
        item.code, item.transcriptDescription ||
        item.longName ||
        item.name,
      ].filter(Boolean).join(' - ') || 'Untitled Specialization'
    }

    function updateValue (value) {
      emit('input', value)
    }

    function updateSpecialization (index, outcome) {
      if (!props.nestedMode) {
        updateValue(putAt(specializations.value, index, outcome))
      }
    }

    function addSpecialization () {
      updateValue([ ...(specializations.value || []), newSpecialization() ])
      editing.value = specializations.value?.length || 0
    }

    function newSpecialization () {
      const emptySpecialization = {}
      return fillSubfieldsWithDefaults(props.config.fields, emptySpecialization)
    }
    function deleteSpecialization (index) {
      updateValue(specializations.value.filter((v, i) => i !== index))
    }
    function selectRow (index) {
      editing.value = index
    }

    return {
      editing,
      selectRow,
      isLoading,
      specializations,
      updateSpecialization,
      deleteSpecialization,
      addSpecialization,
      getSpecializationName,
    }
  },
  computed: {
    ...mapGetters([ 'user' ]),
    userAllowedToEdit () {
      return userHasAnyAllowedRole(this.user, null, this.rolesAllowedToEdit)
    },
  },
  watch: {
    disabled () {
      if (!this.disabled) this.$emit('blur')
    },
  },
  methods: {
    deleteItem (index) {
      this.$confirm({
        header: 'Removing Specialization',
        message: `Are you sure you want to delete <strong>${this.getSpecializationName(this.value[index])}</strong>?`,
        onConfirm: close => {
          close()
          this.deleteSpecialization(index)
        },
        onCancel: close => close(),
      })
    },
    addItem (item = {}) {
      if (!item.id) item.id = this.generateToken(16)
      this.$emit('input', [ ...(this.value || []), item ])
      this.editingIndex = this.value?.length || 0 // Setting it to length because it'll take a little time to get the updated value from the parent
    },
  },
}
</script>
